import {
  Condition,
  ConditionType,
  CoolingOffCondition,
  CoolingOffType,
  DayType,
  DepositPayable as CampaignsDepositPayable,
  DepositType as CampaignsDepositType,
  NoCoolingOffReason,
  SpecialConditionDataConditionType,
  TimeUnit,
  ValueType,
  type Deposit as CampaignsDeposit,
  type FormCondition as CampaignsFormCondition,
  type CustomConditionModel as CampaignsCustomConditionModel,
  type SpecialConditionData as CampaignsSpecialConditionData,
  type StaffUpdateContractDatesRequest,
} from '@urbanx/agx.campaigns.client';
import {
  CoolingOffDetailsCoolingOffType,
  CoolingOffDetailsNoCoolingOffReason,
  DepositCondition,
  DepositConditionType,
  DepositDayType,
  DepositFromDateTimeUnit,
  DepositPayable,
  DepositType,
  DepositValueType,
  FormConditionCondition,
  FormConditionConditionType,
  FormConditionDayType,
  FormConditionFromDateTimeUnit,
  SpecialConditionDataConditionType as TaskSpecialConditionDataConditionType,
  WaiverOfCoolingOffCoolingOffCondition,
  type ContractInformationDatesAndConditionsProposal,
  type ContractInformationDepositsProposal,
  type ContractInformationSpecialConditionsProposal,
  type FormCondition,
  type CustomConditionModel,
  type SpecialConditionData,
} from '@urbanx/agx.activities.client';

const unsupportedEnumValue = (value: never): never => {
  throw new Error(`Unsupported contract information enum value: ${value}`);
};

const toCoolingOffType = (value: CoolingOffType | undefined) => {
  switch (value) {
    case CoolingOffType.FiveDays:
      return CoolingOffDetailsCoolingOffType.FiveDays;
    case CoolingOffType.TenDays:
      return CoolingOffDetailsCoolingOffType.TenDays;
    case CoolingOffType.NDays:
      return CoolingOffDetailsCoolingOffType.NDays;
    case CoolingOffType.NoCoolingOff:
      return CoolingOffDetailsCoolingOffType.NoCoolingOff;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toNoCoolingOffReason = (value: NoCoolingOffReason | undefined) => {
  switch (value) {
    case NoCoolingOffReason.Waived:
      return CoolingOffDetailsNoCoolingOffReason.Waived;
    case NoCoolingOffReason.Exempt:
      return CoolingOffDetailsNoCoolingOffReason.Exempt;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toWaiverCondition = (value: CoolingOffCondition | undefined) => {
  switch (value) {
    case CoolingOffCondition.IncludeShorteningOfCoolingOff:
      return WaiverOfCoolingOffCoolingOffCondition.IncludeShorteningOfCoolingOff;
    case CoolingOffCondition.IncludeWaiverOfCoolingOff:
      return WaiverOfCoolingOffCoolingOffCondition.IncludeWaiverOfCoolingOff;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositPayable = (value: CampaignsDepositPayable | undefined) => {
  switch (value) {
    case CampaignsDepositPayable.On:
      return DepositPayable.On;
    case CampaignsDepositPayable.NDaysFrom:
      return DepositPayable.NDaysFrom;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositType = (value: CampaignsDepositType | undefined) => {
  switch (value) {
    case CampaignsDepositType.Initial:
      return DepositType.Initial;
    case CampaignsDepositType.Balance:
      return DepositType.Balance;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositValueType = (value: ValueType | undefined) => {
  switch (value) {
    case ValueType.Dollar:
      return DepositValueType.Dollar;
    case ValueType.Percentage:
      return DepositValueType.Percentage;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toFormCondition = (value: Condition | undefined) => {
  switch (value) {
    case Condition.OnDate:
      return FormConditionCondition.OnDate;
    case Condition.NdaysFromDate:
      return FormConditionCondition.NdaysFromDate;
    case Condition.OnSpecificDate:
      return FormConditionCondition.OnSpecificDate;
    case Condition.UponSatisfactionOfSpecialCondition:
      return FormConditionCondition.UponSatisfactionOfSpecialCondition;
    case Condition.AdditionalReason:
      return FormConditionCondition.AdditionalReason;
    case Condition.NotApplicable:
      return FormConditionCondition.NotApplicable;
    case Condition.NotSubjectToFinance:
      return FormConditionCondition.NotSubjectToFinance;
    case Condition.Other:
      return FormConditionCondition.Other;
    case Condition.AsPerContract:
      return FormConditionCondition.AsPerContract;
    case Condition.BuildingAndPestBeingSatisfied:
      return FormConditionCondition.BuildingAndPestBeingSatisfied;
    case Condition.FinanceBeingSatisfied:
      return FormConditionCondition.FinanceBeingSatisfied;
    case Condition.OnCoolingOffDate:
      return FormConditionCondition.OnCoolingOffDate;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositCondition = (value: Condition | undefined) => {
  switch (value) {
    case Condition.OnDate:
      return DepositCondition.OnDate;
    case Condition.NdaysFromDate:
      return DepositCondition.NdaysFromDate;
    case Condition.OnSpecificDate:
      return DepositCondition.OnSpecificDate;
    case Condition.UponSatisfactionOfSpecialCondition:
      return DepositCondition.UponSatisfactionOfSpecialCondition;
    case Condition.AdditionalReason:
      return DepositCondition.AdditionalReason;
    case Condition.NotApplicable:
      return DepositCondition.NotApplicable;
    case Condition.NotSubjectToFinance:
      return DepositCondition.NotSubjectToFinance;
    case Condition.Other:
      return DepositCondition.Other;
    case Condition.AsPerContract:
      return DepositCondition.AsPerContract;
    case Condition.BuildingAndPestBeingSatisfied:
      return DepositCondition.BuildingAndPestBeingSatisfied;
    case Condition.FinanceBeingSatisfied:
      return DepositCondition.FinanceBeingSatisfied;
    case Condition.OnCoolingOffDate:
      return DepositCondition.OnCoolingOffDate;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toFormConditionType = (value: ConditionType | undefined) => {
  switch (value) {
    case ConditionType.Contract:
      return FormConditionConditionType.Contract;
    case ConditionType.Unconditional:
      return FormConditionConditionType.Unconditional;
    case ConditionType.Finance:
      return FormConditionConditionType.Finance;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositConditionType = (value: ConditionType | undefined) => {
  switch (value) {
    case ConditionType.Contract:
      return DepositConditionType.Contract;
    case ConditionType.Unconditional:
      return DepositConditionType.Unconditional;
    case ConditionType.Finance:
      return DepositConditionType.Finance;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toFormDayType = (value: DayType | undefined) => {
  switch (value) {
    case DayType.Days:
      return FormConditionDayType.Days;
    case DayType.BusinessDays:
      return FormConditionDayType.BusinessDays;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositDayType = (value: DayType | undefined) => {
  switch (value) {
    case DayType.Days:
      return DepositDayType.Days;
    case DayType.BusinessDays:
      return DepositDayType.BusinessDays;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toFormTimeUnit = (value: TimeUnit | undefined) => {
  switch (value) {
    case TimeUnit.Days:
      return FormConditionFromDateTimeUnit.Days;
    case TimeUnit.Weeks:
      return FormConditionFromDateTimeUnit.Weeks;
    case TimeUnit.Months:
      return FormConditionFromDateTimeUnit.Months;
    case TimeUnit.Years:
      return FormConditionFromDateTimeUnit.Years;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toDepositTimeUnit = (value: TimeUnit | undefined) => {
  switch (value) {
    case TimeUnit.Days:
      return DepositFromDateTimeUnit.Days;
    case TimeUnit.Weeks:
      return DepositFromDateTimeUnit.Weeks;
    case TimeUnit.Months:
      return DepositFromDateTimeUnit.Months;
    case TimeUnit.Years:
      return DepositFromDateTimeUnit.Years;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toTaskFormCondition = (
  condition: CampaignsFormCondition | undefined
): FormCondition | undefined =>
  condition && {
    depositPayable: toDepositPayable(condition.depositPayable),
    daysFromDate: condition.daysFromDate,
    dayType: toFormDayType(condition.dayType),
    fromDateTimeUnit: toFormTimeUnit(condition.fromDateTimeUnit),
    referenceDate: condition.referenceDate,
    timeOfDay: condition.timeOfDay,
    completedDate: condition.completedDate,
    conditionType: toFormConditionType(condition.conditionType),
    condition: toFormCondition(condition.condition),
    specialConditionId: condition.specialConditionId,
    other: condition.other,
  };

const toTaskDeposit = (
  deposit: CampaignsDeposit
): NonNullable<ContractInformationDepositsProposal['deposits']>[number] => ({
  depositValueType: toDepositValueType(deposit.depositValueType),
  depositType: toDepositType(deposit.depositType),
  isDepositSelected: deposit.isDepositSelected,
  amount: deposit.amount,
  percent: deposit.percent,
  depositPayable: toDepositPayable(deposit.depositPayable),
  daysFromDate: deposit.daysFromDate,
  dayType: toDepositDayType(deposit.dayType),
  fromDateTimeUnit: toDepositTimeUnit(deposit.fromDateTimeUnit),
  referenceDate: deposit.referenceDate,
  timeOfDay: deposit.timeOfDay,
  completedDate: deposit.completedDate,
  conditionType: toDepositConditionType(deposit.conditionType),
  condition: toDepositCondition(deposit.condition),
  specialConditionId: deposit.specialConditionId,
  other: deposit.other,
});

const toSpecialConditionType = (value: SpecialConditionDataConditionType | undefined) => {
  switch (value) {
    case SpecialConditionDataConditionType.Custom:
      return TaskSpecialConditionDataConditionType.Custom;
    case SpecialConditionDataConditionType.SellerToInstallSmokeAlarms:
      return TaskSpecialConditionDataConditionType.SellerToInstallSmokeAlarms;
    case SpecialConditionDataConditionType.SellerToObtainPoolSafetyCertificate:
      return TaskSpecialConditionDataConditionType.SellerToObtainPoolSafetyCertificate;
    case SpecialConditionDataConditionType.NoticeOfNoFunctioningBodyCorporate:
      return TaskSpecialConditionDataConditionType.NoticeOfNoFunctioningBodyCorporate;
    case SpecialConditionDataConditionType.SubjectToBodyCorporateApprovalOfPet:
      return TaskSpecialConditionDataConditionType.SubjectToBodyCorporateApprovalOfPet;
    case SpecialConditionDataConditionType.DueDiligence:
      return TaskSpecialConditionDataConditionType.DueDiligence;
    case SpecialConditionDataConditionType.RegistrationOfRecordOfDeath:
      return TaskSpecialConditionDataConditionType.RegistrationOfRecordOfDeath;
    case SpecialConditionDataConditionType.SubjectToSaleUnconditionalAndSettlement:
      return TaskSpecialConditionDataConditionType.SubjectToSaleUnconditionalAndSettlement;
    case SpecialConditionDataConditionType.SubjectToUnconditionalAndSettlement:
      return TaskSpecialConditionDataConditionType.SubjectToUnconditionalAndSettlement;
    case SpecialConditionDataConditionType.SubjectToSettlement:
      return TaskSpecialConditionDataConditionType.SubjectToSettlement;
    case SpecialConditionDataConditionType.SunsetClause:
      return TaskSpecialConditionDataConditionType.SunsetClause;
    case SpecialConditionDataConditionType.TransmissionByDeath:
      return TaskSpecialConditionDataConditionType.TransmissionByDeath;
    case SpecialConditionDataConditionType.TerminationOfPriorContract:
      return TaskSpecialConditionDataConditionType.TerminationOfPriorContract;
    case SpecialConditionDataConditionType.EarlyReleaseOfDepositToSeller:
      return TaskSpecialConditionDataConditionType.EarlyReleaseOfDepositToSeller;
    case SpecialConditionDataConditionType.PropertyAtSellersRiskUntilSettlement:
      return TaskSpecialConditionDataConditionType.PropertyAtSellersRiskUntilSettlement;
    case undefined:
      return undefined;
    default:
      return unsupportedEnumValue(value);
  }
};

const toTaskCustomCondition = (condition: CampaignsCustomConditionModel): CustomConditionModel => ({
  clauseId: condition.clauseId,
  textInput: condition.textInput,
  overriddenDueDate: toTaskFormCondition(condition.overriddenDueDate),
  subClauses: condition.subClauses?.map(toTaskCustomCondition),
});

const toTaskSpecialCondition = (
  condition: CampaignsSpecialConditionData
): SpecialConditionData => ({
  id: condition.id,
  conditionType: toSpecialConditionType(condition.conditionType),
  overriddenClauseNumber: condition.overriddenClauseNumber,
  clauses: condition.clauses?.map((clause) => ({
    clauseId: clause.clauseId,
    overriddenDueDate: toTaskFormCondition(clause.overriddenDueDate),
    dataPoints: clause.dataPoints?.map((dataPoint) => ({
      dataPointId: dataPoint.dataPointId,
      model: dataPoint.model,
    })),
  })),
  customCondition: condition.customCondition
    ? toTaskCustomCondition(condition.customCondition)
    : undefined,
});

export const toDatesAndConditionsTaskProposal = (
  request: StaffUpdateContractDatesRequest
): ContractInformationDatesAndConditionsProposal => ({
  contractDate: request.contractDate,
  coolingOffDetails: request.coolingOffDetails
    ? {
        coolingOffType: toCoolingOffType(request.coolingOffDetails.coolingOffType),
        coolingOffDays: request.coolingOffDetails.coolingOffDays,
        noCoolingOffReason: toNoCoolingOffReason(request.coolingOffDetails.noCoolingOffReason),
      }
    : undefined,
  waiverOfCoolingOff: request.waiverOfCoolingOff
    ? {
        coolingOffCondition: toWaiverCondition(request.waiverOfCoolingOff.coolingOffCondition),
        shortenCoolingOffDays: request.waiverOfCoolingOff.shortenCoolingOffDays,
      }
    : undefined,
  subjectToFinance: toTaskFormCondition(request.subjectToFinance),
  buildingAndPestCondition: toTaskFormCondition(request.buildingAndPestCondition),
  bodyCorporateRecordsCondition: toTaskFormCondition(request.bodyCorporateRecordsCondition),
});

export const toDepositsTaskProposal = (
  deposits: CampaignsDeposit[]
): ContractInformationDepositsProposal => ({
  deposits: deposits.map(toTaskDeposit),
});

export const toSpecialConditionsTaskProposal = (
  specialConditions: CampaignsSpecialConditionData[]
): ContractInformationSpecialConditionsProposal => ({
  specialConditions: specialConditions.map(toTaskSpecialCondition),
});
